import { useState, type FormEvent } from "react";
import { hotEvents, type Category } from "@/mocks/events";

const SUBMIT_URL = "https://readdy.ai/api/form/daeomk3ep053ijub1fb0";

function scrollToId(href: string) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const CHANNEL_NAMES: Category[] = ["全部", "娱乐热点", "科技互联网", "商业财经", "社会民生", "直播电商", "文化综艺"];

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formNotice, setFormNotice] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const honeypot = (form.querySelector('input[name="contact_alt"]') as HTMLInputElement | null)?.value?.trim();
    if (honeypot) {
      setStatus("success");
      setFormNotice("订阅成功，我们会把最近的吃瓜热点推给你，感谢关注！");
      return;
    }

    const data = new FormData(form);
    const email = String(data.get("email") || "").trim();
    if (!email) {
      setFormError("请先填写你的邮箱地址。");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setFormError("");
    const payload = new URLSearchParams();
    payload.set("email", email);

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (responseText && !parsed ? responseText.slice(0, 200) : "");
      const isSpam = /spam/i.test(responseText + (serverMsg || ""));

      if (res.ok && code === "OK" && !isSpam) {
        form.reset();
        setStatus("success");
        setFormNotice("订阅成功，欢迎加入「瓜记」资讯！最近的热点时间线会定期推送到你的邮箱。");
      } else {
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : isSpam
              ? "看起来本次提交被判定为疑似垃圾信息，请核对后再试一次。"
              : "提交失败，请稍后再试或换一个邮箱地址。"
        );
        setStatus("error");
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎开小差了，请检查后重试。");
    }
  };

  return (
    <footer id="contact" className="border-t border-background-200 bg-background-100">
      {/* subscribe band */}
      <div className="mx-auto w-full max-w-7xl px-4 pb-10 pt-12 md:px-6 md:pt-16">
        <div className="relative overflow-hidden rounded-3xl bg-primary-600 p-6 md:p-10">
          <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-accent-400/40 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-background-50/10 blur-2xl" />
          <div className="relative grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div>
              <h2 className="font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
                别错过下一颗「大瓜」
                <br className="hidden sm:block" />
                订阅热点时间线更新
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-background-100">
                每有新事件进入热榜、出现反转或给出结论，我们都会第一时间整理成时间线。填写邮箱即可接收精选推送，不打扰、不群发。
              </p>
            </div>

            <form className="subscribe-panel relative" data-readdy-form onSubmit={handleSubmit} noValidate>
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <div className="flex flex-col gap-3 sm:flex-row">
                <label className="sr-only" htmlFor="subscribe-email">
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  name="email"
                  type="email"
                  required
                  placeholder="输入你的邮箱地址"
                  autoComplete="email"
                  className="h-12 w-full rounded-md border border-background-50/20 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:ring-2 focus:ring-accent-300"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex h-12 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      订阅吃瓜更新
                    </>
                  )}
                </button>
              </div>
              {status === "success" ? (
                <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-accent-100" role="status">
                  <i className="ri-checkbox-circle-fill" />
                  {formNotice}
                </p>
              ) : null}
              {status === "error" ? (
                <p className="mt-3 flex items-start gap-1.5 text-sm font-medium text-background-100" role="alert">
                  <i className="ri-error-warning-line mt-0.5" />
                  {formError}
                </p>
              ) : null}
            </form>
          </div>
        </div>
      </div>

      {/* footer columns */}
      <div className="border-t border-background-200 bg-background-100">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4 md:px-6">
          <div className="sm:col-span-2 lg:col-span-1">
            <a
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="flex items-center gap-2.5"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg font-bold text-background-50">
                瓜
              </span>
              <span className="font-heading text-xl font-bold text-foreground-900">瓜记</span>
            </a>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              全网吃瓜事件时间线整理平台，把散落的复杂瓜事，捋成清晰的前因后果与完整经过，助你理性看懂每一场热点。
            </p>
            <a
              href="mailto:hello@readdy.ai"
              rel="nofollow"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-600"
            >
              <i className="ri-mail-line" />
              hello@readdy.ai
            </a>
          </div>

          <div>
            <h3 className="text-sm font-bold text-foreground-900">快速浏览</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-600">
              {[
                { t: "今日热榜", href: "#hot" },
                { t: "分类频道", href: "#channels" },
                { t: "深度时间线", href: "#deep" },
                { t: "吃瓜专栏", href: "#columns" },
              ].map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId(l.href);
                    }}
                    className="inline-flex items-center gap-1 transition-colors hover:text-primary-700"
                  >
                    {l.t}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold text-foreground-900">追哪些瓜</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-600">
              {CHANNEL_NAMES.slice(1).map((c) => {
                const count = hotEvents.filter((e) => e.category === c).length;
                return (
                  <li key={c} className="flex items-center justify-between">
                    <a
                      href="#channels"
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToId("#channels");
                      }}
                      className="transition-colors hover:text-primary-700"
                    >
                      {c}
                    </a>
                    <span className="text-xs text-foreground-400">{count}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-bold text-foreground-900">平台说明</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-600">
              {[
                { t: "关于我们与原则", href: "#about" },
                { t: "常见问题", href: "#faq" },
                { t: "订阅热点更新", href: "#contact" },
                { t: "返回顶部", href: "#top" },
              ].map((l) => (
                <li key={l.t}>
                  <a
                    href={l.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId(l.href);
                    }}
                    className="inline-flex items-center gap-1 transition-colors hover:text-primary-700"
                  >
                    {l.t}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-background-200">
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-4 py-6 text-xs text-foreground-500 sm:flex-row sm:items-center sm:justify-between md:px-6">
            <p>© {new Date().getFullYear()} 瓜记 · 全网吃瓜事件时间线整理。仅用于内容展示与信息整理。</p>
            <p className="inline-flex items-center gap-1.5">
              <i className="ri-shield-check-line text-secondary-700" />
              内容为公开信息的中立整理，不代表任何官方立场，请以权威发布为准。
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}